import { useRouter } from 'expo-router';
import React, { useCallback, useState } from 'react';
import {
  Alert,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AppVersionFooter } from '@/components/settings/AppVersionFooter';
import { SettingItem } from '@/components/settings/SettingItem';
import { SettingSection } from '@/components/settings/SettingSection';
import { ThemePicker } from '@/components/settings/ThemePicker';
import { ScreenHeaderContainer } from '@/components/ui/ScreenHeaderContainer';
import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { getHeadingFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useNotificationStatusLabel } from '@/hooks/use-notification-status-label';
import { reportContextStore } from '@/lib/report/ReportContextStore';
import { useSupabaseAuth } from '@/lib/supabase';
import { useTheme } from '@/lib/theme';

/**
 * Settings Screen
 * Displays app settings, legal information, and sign out option.
 * Provides navigation to AI summaries, disclaimers, and terms pages.
 */
export default function SettingsScreen() {
  const colorScheme = useColorScheme();
  const router = useRouter();
  const { signOut } = useSupabaseAuth();
  const { themePreference, setThemePreference } = useTheme();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [showThemePicker, setShowThemePicker] = useState(false);
  const notificationStatusLabel = useNotificationStatusLabel();

  const accentColor = Colors[colorScheme ?? 'light'].tint;
  const backgroundColor = Colors[colorScheme ?? 'light'].background;
  const textColor = Colors[colorScheme ?? 'light'].text;

  const handleSignOut = useCallback(async () => {
    Alert.alert('Sign Out', 'Are you sure you want to sign out?', [
      {
        text: 'Cancel',
        style: 'cancel',
      },
      {
        text: 'Sign Out',
        style: 'destructive',
        onPress: async () => {
          setIsSigningOut(true);
          const { error: signOutError } = await signOut();
          setIsSigningOut(false);

          if (signOutError) {
            Alert.alert('Error', 'Failed to sign out. Please try again.');
            console.error('[settings:sign-out]', signOutError.message);
          }
        },
      },
    ]);
  }, [signOut]);

  const handleNavigateToDisclaimers = useCallback(() => {
    router.push('/disclaimers');
  }, [router]);

  const handleNavigateToTerms = useCallback(() => {
    router.push('/terms');
  }, [router]);

  const handleNavigateToPrivacyPolicy = useCallback(() => {
    router.push('/privacy-policy');
  }, [router]);

  const handleNavigateToAISummaries = useCallback(() => {
    router.push('/ai-summaries');
  }, [router]);

  const handleNavigateToReportAIContent = useCallback(() => {
    // Generic entry point — drop any content stashed by an AI surface so this
    // opens a context-free report.
    reportContextStore.clear();
    router.push('/report-ai-content');
  }, [router]);

  const handleNotificationsSetting = useCallback(() => {
    router.push('/notification-settings');
  }, [router]);

  const handleDefaultCalendarSetting = useCallback(() => {
    router.push('/default-calendar');
  }, [router]);

  const handleThemeSetting = useCallback(() => {
    setShowThemePicker(true);
  }, []);

  const handleNavigateToYourAccount = useCallback(() => {
    router.push('/your-account');
  }, [router]);

  const getThemeLabel = (theme: string) => {
    switch (theme) {
      case 'system':
        return 'System';
      case 'light':
        return 'Light';
      case 'dark':
        return 'Dark';
      default:
        return 'System';
    }
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor }]} edges={['bottom']}>
      <ScreenHeaderContainer style={styles.header} flatBottom>
        <View style={styles.headerRow}>
          <Text style={[styles.headerTitle, { color: textColor, fontFamily: getHeadingFont() }]}>
            Settings
          </Text>
          <AppVersionFooter compact />
        </View>
      </ScreenHeaderContainer>
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent}>
        <SettingSection textColor={textColor}>
          <SettingItem
            icon="sparkles-outline"
            iconColor={accentColor}
          label="AI Summaries"
          onPress={handleNavigateToAISummaries}
          textColor={textColor}
          isFirst
        />
        <SettingItem
          icon="notifications-outline"
          iconColor={accentColor}
          label="Notifications"
          value={notificationStatusLabel}
          onPress={handleNotificationsSetting}
          textColor={textColor}
        />
        <SettingItem
          icon="calendar-outline"
          iconColor={accentColor}
          label="Default Calendar"
          onPress={handleDefaultCalendarSetting}
          textColor={textColor}
        />
        <SettingItem
          icon="color-palette-outline"
          iconColor={accentColor}
          label="Theme"
          value={getThemeLabel(themePreference)}
          onPress={handleThemeSetting}
          textColor={textColor}
          isLast
        />
      </SettingSection>

      <SettingSection title="Legal" textColor={textColor}>
        <SettingItem
          icon="flag-outline"
          iconColor={accentColor}
          label="Report AI Content"
          onPress={handleNavigateToReportAIContent}
          textColor={textColor}
          isFirst
        />
        <SettingItem
          icon="shield-checkmark-outline"
          iconColor={accentColor}
          label="Disclaimers"
          onPress={handleNavigateToDisclaimers}
          textColor={textColor}
        />
        <SettingItem
          icon="lock-closed-outline"
          iconColor={accentColor}
          label="Privacy Policy"
          onPress={handleNavigateToPrivacyPolicy}
          textColor={textColor}
        />
        <SettingItem
          icon="document-text-outline"
          iconColor={accentColor}
          label="Terms & Conditions"
          onPress={handleNavigateToTerms}
          textColor={textColor}
          isLast
        />
      </SettingSection>

      <SettingSection title="Account" textColor={textColor}>
        <SettingItem
          icon="person-circle-outline"
          iconColor={accentColor}
          label="Your Account"
          onPress={handleNavigateToYourAccount}
          textColor={textColor}
          isFirst
        />
        <SettingItem
          icon="log-out-outline"
          iconColor="#ff3b30"
          label={isSigningOut ? 'Signing out...' : 'Sign Out'}
          onPress={handleSignOut}
          showChevron={false}
          textColor={textColor}
          isDanger
          isLast
        />
      </SettingSection>
      </ScrollView>

      <ThemePicker
        visible={showThemePicker}
        currentTheme={themePreference}
        onClose={() => setShowThemePicker(false)}
        onSelectTheme={setThemePreference}
        colorScheme={colorScheme}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: Spacing.xl,
    paddingTop: Spacing.md,
    paddingBottom: Spacing.lg,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    fontSize: 26,
    fontWeight: '600',
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    flexGrow: 1,
    paddingTop: Spacing.lg,
  },
});
